// Reglas que bloquean el guardado de «Cuentas por contrato» (ACUERDOS, sección D, puntos 9 a 15).
//
// Es la misma lógica de `services/cuentas_contrato_reglas.js` de kredit-api, portada a TypeScript como funciones puras:
// no tocan nada, reciben un estado y devuelven la lista de bloqueos. La prueba (cuentas-contrato-reglas.spec.ts)
// recorre los MISMOS casos que el núcleo (fixtures/cuentas-contrato-casos.json). Si cambia una regla allá, se cambia
// aquí y se vuelve a copiar el fixture (ver fixtures/README.md).
//
// Estado:
//   perfilCobra  'MT' | 'CT' | ... | null   contrato que cobra (null = no se pudo leer; no se inventa)
//   opciones     [{ _id, code?, title?, showClient, priority }]
//   cuentas      [{ _id, code?, payment_option, payment_method, bank, perfil, equivale_a, status, showClient,
//                   showChatbot, typeConfirm }]   (payment_method = código del método; bank = _id o código)
//   cobros       [{ servicio, opcion, cuentaMT, cuentaCT }]   cobros fijos de botones y DI: _id de la cuenta MT y
//                de su par del contrato (null si no tiene). Esas cuentas no se «eligen»: las toma el servicio.

export interface BloqueoCuenta {
  codigo: string;
  mensaje: string;
  opcion?: string;
  cuenta?: string;
}

export const METODOS_PAGO_MOVIL_Y_TRANSFERENCIA = ['PAGO_MOVIL', 'TRANSFERENCIA'];

const id = (v: any): string | null =>
  v === undefined || v === null ? null : String(v && v.toHexString ? v.toHexString() : (v._id !== undefined ? v._id : v));

/** Perfil de una cuenta: sin marca es MT, como en el núcleo. */
export const perfilDe = (c: any): string => {
  const t = String(c && c.perfil !== undefined && c.perfil !== null ? c.perfil : '').trim().toUpperCase();
  return t || 'MT';
};

const metodoDe = (c: any): string | null => {
  const m = c.payment_method;
  return m && typeof m === 'object' && !m.toHexString ? (m.code || id(m)) : id(m);
};

/** Lo que el cliente ve: cuenta no apagada con showClient. Un status sin dato (legado) cuenta como activa, como el default del modelo. */
export const esVisible = (c: any): boolean => c.status !== false && c.showClient === true;

const nombreDe = (c: any): string => c.code || (id(c._id) as string);
const tituloDe = (o: any): string => o.title || o.code || (id(o._id) as string);

/** Cuentas que el cliente elige: todas menos las que toma un servicio fijo (botones y DI). */
export const cuentasElegibles = (estado: any): any[] => {
  const fijas = new Set<string | null>();
  for (const co of estado.cobros || []) { fijas.add(id(co.cuentaMT)); fijas.add(id(co.cuentaCT)); }
  return (estado.cuentas || []).filter((c: any) => !fijas.has(id(c._id)));
};

/** 9. Ninguna opción de pago visible. */
const reglaSinOpcionVisible = (estado: any): BloqueoCuenta[] =>
  (estado.opciones || []).some((o: any) => o.showClient === true)
    ? []
    : [{ codigo: 'sin_opcion_visible', mensaje: 'Ninguna opción de pago queda visible en la app: el cliente no tendría cómo pagar.' }];

/** 10. Opción visible con cuentas para elegir y ninguna visible (se abriría vacía). */
const reglaOpcionVacia = (estado: any): BloqueoCuenta[] => {
  const elegibles = cuentasElegibles(estado);
  const bloqueos: BloqueoCuenta[] = [];
  for (const o of estado.opciones || []) {
    if (o.showClient !== true) continue;
    const suyas = elegibles.filter((c) => id(c.payment_option) === id(o._id));
    if (suyas.length && !suyas.some(esVisible)) {
      bloqueos.push({ codigo: 'opcion_vacia', opcion: id(o._id) as string,
        mensaje: `«${tituloDe(o)}» se ve en la app pero no tiene ninguna cuenta visible: el cliente la abriría vacía.` });
    }
  }
  return bloqueos;
};

/** 11. Dos cuentas visibles del mismo banco en el mismo método. */
const reglaBancoRepetido = (estado: any): BloqueoCuenta[] => {
  const grupos = new Map<string, any[]>();
  for (const c of cuentasElegibles(estado).filter(esVisible)) {
    const banco = id(c.bank);
    if (!banco) continue;
    const clave = `${metodoDe(c)}|${banco}`;
    if (!grupos.has(clave)) grupos.set(clave, []);
    (grupos.get(clave) as any[]).push(c);
  }
  const bloqueos: BloqueoCuenta[] = [];
  grupos.forEach((lista) => {
    if (lista.length < 2) return;
    bloqueos.push({ codigo: 'banco_repetido', cuenta: id(lista[0]._id) as string, opcion: id(lista[0].payment_option) || undefined,
      mensaje: `En ${metodoDe(lista[0])}, el cliente vería ${lista.length} cuentas del mismo banco (${lista.map(nombreDe).join(', ')}) y podría pagar a la que no es.` });
  });
  return bloqueos;
};

/** 12. Cuenta visible de un contrato distinto al que cobra. Con el contrato desconocido, lo informa. */
const reglaContratoDistinto = (estado: any): BloqueoCuenta[] => {
  const visibles = cuentasElegibles(estado).filter(esVisible);
  if (!estado.perfilCobra) {
    return visibles.length
      ? [{ codigo: 'contrato_desconocido', mensaje: 'No se pudo leer qué contrato cobra: no se puede comprobar que las cuentas visibles sean del contrato correcto.' }]
      : [];
  }
  const cobra = String(estado.perfilCobra).toUpperCase();
  return visibles.filter((c) => perfilDe(c) !== cobra).map((c) => ({
    codigo: 'contrato_distinto', cuenta: id(c._id) as string, opcion: id(c.payment_option) || undefined,
    mensaje: cobra === 'MT'
      ? `La cuenta ${nombreDe(c)} (${perfilDe(c)}) se ve antes del encendido: el cliente le pagaría a ${perfilDe(c)} mientras cobra MT.`
      : `La cuenta ${nombreDe(c)} (${perfilDe(c)}) sigue visible con ${cobra} cobrando.`
  }));
};

/** 13. Cuenta visible sin verificación configurada (typeConfirm). */
const reglaSinVerificacion = (estado: any): BloqueoCuenta[] =>
  cuentasElegibles(estado).filter(esVisible).filter((c) => !String(c.typeConfirm || '').trim()).map((c) => ({
    codigo: 'sin_verificacion', cuenta: id(c._id) as string, opcion: id(c.payment_option) || undefined,
    mensaje: `La cuenta ${nombreDe(c)} se muestra sin verificación configurada: sus pagos no se podrían conciliar.`
  }));

/**
 * 14. Botón o DI sin cuenta activa del contrato que cobra. Solo si su opción está visible.
 * Es aviso, no bloqueo (Mario, 27-oct: «debe dejarme, no me restrinjas tanto porque me bloquea lo que quiero probar»):
 * sale en avisar(). Antes bloqueaba el guardado.
 */
const reglaCobroSinCuenta = (estado: any): BloqueoCuenta[] => {
  const visibles = (estado.cobros || []).filter((co: any) => (estado.opciones || []).some((o: any) => id(o._id) === id(co.opcion) && o.showClient === true));
  if (!estado.perfilCobra) {
    return visibles.length
      ? [{ codigo: 'contrato_desconocido', mensaje: 'No se pudo leer qué contrato cobra: no se puede comprobar que los botones y el DI tengan su cuenta activa.' }]
      : [];
  }
  const cobraMT = String(estado.perfilCobra).toUpperCase() === 'MT';
  const bloqueos: BloqueoCuenta[] = [];
  for (const co of visibles) {
    const objetivo = id(cobraMT ? co.cuentaMT : co.cuentaCT);
    const cuenta = objetivo ? (estado.cuentas || []).find((c: any) => id(c._id) === objetivo) : null;
    if (cuenta && cuenta.status !== false) continue;
    bloqueos.push({ codigo: 'cobro_sin_cuenta', opcion: id(co.opcion) as string, ...(cuenta ? { cuenta: objetivo as string } : {}),
      mensaje: `${co.servicio}: no hay cuenta ${cobraMT ? 'MT' : String(estado.perfilCobra).toUpperCase()} activa. Con este contrato, ese cobro fallaría.` });
  }
  return bloqueos;
};

/**
 * 15. (13-oct) Cuenta MT de pago móvil o transferencia visible (app o chatbot) sin par del perfil: frena el switch.
 * No frena el guardado: va aparte (revisar con { switch: true }) y el GET la devuelve en `frenosSwitch`.
 */
export const reglaMtSinPar = (estado: any, perfilDestino = 'CT'): BloqueoCuenta[] => {
  const cuentas: any[] = estado.cuentas || [];
  return cuentas
    .filter((c) => perfilDe(c) === 'MT' && METODOS_PAGO_MOVIL_Y_TRANSFERENCIA.includes(metodoDe(c) as string) && (c.showClient === true || c.showChatbot === true))
    .filter((c) => !cuentas.some((p) => perfilDe(p) === perfilDestino && p.equivale_a === c.code))
    .map((c) => ({ codigo: 'mt_sin_par', cuenta: id(c._id) as string, opcion: id(c.payment_option) || undefined,
      mensaje: `La cuenta MT ${nombreDe(c)} es visible y no tiene su par ${perfilDestino}: el corte la dejaría visible.` }));
};

const limpiar = (b: any): BloqueoCuenta => Object.fromEntries(Object.entries(b).filter(([, v]) => v !== undefined)) as any;

/**
 * Revisa un estado. Orden: 9, 10, 11, 12, 13 (y 15 con { switch: true }). La 14 avisa: ver avisar().
 */
export const revisar = (estado: any, { switch: paraSwitch = false, perfilDestino = 'CT' }: { switch?: boolean; perfilDestino?: string } = {}): BloqueoCuenta[] => {
  return [
    ...reglaSinOpcionVisible(estado), ...reglaOpcionVacia(estado), ...reglaBancoRepetido(estado),
    ...reglaContratoDistinto(estado), ...reglaSinVerificacion(estado),
    ...(paraSwitch ? reglaMtSinPar(estado, perfilDestino) : [])
  ].map(limpiar);
};

/** Lo que se informa sin frenar el guardado: la regla 14. */
export const avisar = (estado: any): BloqueoCuenta[] => reglaCobroSinCuenta(estado).map(limpiar);
